import type { JSONSchema7 } from "json-schema";

import type { CacheType, MessageType } from "./types";

const defaultSystemMessage =
  "You are a helpful assistant. You provide accurate information, clear guidance, and friendly support in a neutral and polite manner to assist users effectively.";

export const systemMessage: MessageType = {
  id: Date.now(),
  type: "default",
  selectedChoice: 0,
  choices: [
    {
      role: "system",
      content: defaultSystemMessage,
    },
  ],
};

export const cacheTypes: CacheType[] = [
  {
    title: "public",
    description:
      "Default behaviour, returns from cache if found, otherwise goes to LLM",
  },
  {
    title: "no-store",
    description:
      "In case answer is returned from LLM will not be stored in the cache",
  },
  {
    title: "no-cache",
    description:
      "Bypasses the cache; the answer is always returned from the LLM",
  },
  {
    title: "only-if-cached",
    description:
      "Retrieves answer only from the cache and if not found it returns 504",
  },
];

export const defaultTools = [
  {
    name: "get_stock_price",
    description: "Get the current stock price",
    strict: true,
    parameters: {
      type: "object",
      properties: {
        symbol: {
          type: "string",
          description: "The stock symbol",
        },
      },
      additionalProperties: false,
      required: ["symbol"],
    },
  },
  {
    name: "get_weather",
    description: "Determine weather in my location",
    strict: true,
    parameters: {
      type: "object",
      properties: {
        location: {
          type: "string",
          description: "The city and state e.g. San Francisco, CA",
        },
        unit: {
          type: "string",
          enum: ["c", "f"],
        },
      },
      additionalProperties: false,
      required: ["location", "unit"],
    },
  },
];

export const toolsValidationSchema: JSONSchema7 = {
  type: "object",
  properties: {
    name: { type: "string", minLength: 1 },
    description: { type: "string", minLength: 1 },
    strict: { type: "boolean" },
    parameters: {
      type: "object",
    },
  },
  required: ["name"],
  additionalProperties: true,
};

export const defaultResponseFormats = [
  {
    name: "math_response",
    strict: true,
    schema: {
      type: "object",
      properties: {
        steps: {
          type: "array",
          items: {
            type: "object",
            properties: {
              explanation: {
                type: "string",
              },
              output: {
                type: "string",
              },
            },
            required: ["explanation", "output"],
            additionalProperties: false,
          },
        },
        final_answer: {
          type: "string",
        },
      },
      additionalProperties: false,
      required: ["steps", "final_answer"],
    },
  },
  {
    name: "paper_metadata",
    strict: true,
    schema: {
      type: "object",
      properties: {
        title: {
          type: "string",
        },
        authors: {
          type: "array",
          items: {
            type: "string",
          },
        },
        abstract: {
          type: "string",
        },
        keywords: {
          type: "array",
          items: {
            type: "string",
          },
        },
      },
      required: ["title", "authors", "abstract", "keywords"],
      additionalProperties: false,
    },
  },
  {
    name: "content_compliance",
    strict: true,
    schema: {
      type: "object",
      properties: {
        violates: {
          type: "boolean",
          description: "Indicates whether the content violates policies.",
        },
        violation_categories: {
          type: "array",
          description: "Categories under which the content violates policies.",
          items: {
            type: "string",
            enum: ["sexual", "violence", "self_harm"],
          },
        },
        violation_reason: {
          type: "string",
          description: "Explanation of why the content violates policies.",
        },
      },
      required: ["violates", "violation_categories", "violation_reason"],
      additionalProperties: false,
    },
  },
];
